import React, { useState } from 'react';
import TmuaPearsonShell, {
  DoorIcon,
  NextIcon,
  PrevIcon,
  TmuaPearsonFooterButton,
  useTmuaPearsonAccessKeys,
  isLnatPearsonExam,
  isEsatPearsonExam,
  isTaraPearsonExam,
  getPearsonPlayerExamTitle,
} from './TmuaPearsonShell';
import TmuaPearsonDialog from './TmuaPearsonDialog';
import { TMUA_PEARSON_TOKENS } from '../constants/tmuaPearsonTokens';
import { getPearsonColorSchemeDropdownLabel } from '../constants/tmuaColorSchemes';
import {
  TmuaPearsonInstructionPage,
  TmuaPearsonInstructionTable,
  NextNCopy,
} from './TmuaPearsonInstructionCopy';
import { buildLnatPearsonOverviewCopy } from '../utils/buildLnatPearsonOverviewCopy';

function emphasizeOne(text) {
  const parts = String(text).split(/\b(one)\b/);
  if (parts.length === 1) return text;
  return parts.map((part, i) => (
    part === 'one' ? <strong key={i}>one</strong> : part
  ));
}

function LnatMockOverviewBody({
  paper1Count,
  paper2Count,
  hasPaper2,
  modeChoice,
  paper1Time,
  paper2Time,
}) {
  const T = TMUA_PEARSON_TOKENS;
  const copy = buildLnatPearsonOverviewCopy({
    sectionACount: paper1Count,
    sectionBCount: hasPaper2 ? paper2Count : 3,
    paper1Time,
    paper2Time,
    untimed: modeChoice !== 'timed',
  });
  const pStyle = { margin: '0 0 10px', color: 'var(--iv-body)' };
  const timeStyle = { margin: '0 0 12px', color: 'var(--iv-body)', fontWeight: 700 };
  return (
    <div
      className="tmua-pearson-instruction-copy"
      style={{
        position: 'absolute',
        inset: 0,
        overflowY: 'auto',
        padding: '18px 22px',
        color: '#000',
        fontFamily: T.fontUi,
        fontSize: 16,
        lineHeight: 1.35,
      }}
    >
      <h1 style={{
        margin: '8px 0 18px',
        textAlign: 'center',
        fontSize: 16,
        fontWeight: 700,
        fontFamily: T.fontUi,
      }}
      >
        {copy.title}
      </h1>
      <p style={pStyle}>{copy.intro}</p>
      <p style={{ ...pStyle, fontWeight: 700 }}>{copy.sectionAHeading}</p>
      {copy.sectionABody.map((line) => (
        <p key={line} style={pStyle}>{emphasizeOne(line)}</p>
      ))}
      <p style={timeStyle}>{copy.sectionATime}</p>
      <p style={{ ...pStyle, fontWeight: 700 }}>{copy.sfrHeading}</p>
      {copy.sfrBody.map((line) => (
        <p key={line} style={pStyle}>{line}</p>
      ))}
      <p style={timeStyle}>{copy.sfrTime}</p>
      <p style={{ ...pStyle, fontWeight: 700 }}>{copy.sectionBHeading}</p>
      {copy.sectionBBody.map((line) => (
        <p key={line} style={pStyle}>{emphasizeOne(line)}</p>
      ))}
      <p style={timeStyle}>{copy.sectionBTime}</p>
      {copy.sectionBNotes.map((line) => (
        <p key={line} style={pStyle}>{line}</p>
      ))}
      <p style={{ ...pStyle, fontWeight: 700 }}>{copy.note}</p>
      <p style={{ ...pStyle, marginBottom: 0 }}>{copy.access}</p>
    </div>
  );
}

export default function TmuaExamOverviewScreen({
  examName = 'TMUA',
  title,
  tsaSitting = false,
  heritageExam,
  paper1Count = 20,
  paper2Count = 20,
  hasPaper2 = true,
  modeChoice,
  paper1Time,
  paper2Time,
  rows,
  bodyCopy,
  colorScheme,
  setColorScheme,
  onNext,
  onPrevious,
  onEndExam,
  lnatMockOverview = false,
}) {
  const [showEndExam, setShowEndExam] = useState(false);
  const T = TMUA_PEARSON_TOKENS;
  const isLnat = isLnatPearsonExam(examName);
  const isEsat = isEsatPearsonExam(examName);
  const isTara = isTaraPearsonExam(examName);
  const nextVariant = isLnat || isEsat || isTara ? 'chrome' : 'yellow';
  const nextIconColor = isLnat || isEsat || isTara ? '#FFFFFF' : T.nextDefaultFg;
  const tmuaTimeCell = modeChoice === 'timed' ? '75 minutes' : 'Untimed';
  const p1 = isLnat
    ? Number(paper1Count) || 0
    : (Number(paper1Count) > 0 ? Number(paper1Count) : 20);
  const p2 = isLnat
    ? Number(paper2Count) || 0
    : (Number(paper2Count) > 0 ? Number(paper2Count) : 20);
  const p1Time = paper1Time || tmuaTimeCell;
  const p2Time = paper2Time || tmuaTimeCell;
  const tableRows = Array.isArray(rows) && rows.length
    ? rows
    : tsaSitting
      ? [
          {
            section: 'Section 1',
            questions: Number(paper1Count) > 0 ? Number(paper1Count) : 50,
            time: p1Time,
          },
          ...(hasPaper2 ? [{
            section: 'Section 2',
            questions: Number(paper2Count) > 0 ? Number(paper2Count) : 1,
            time: p2Time,
          }] : []),
        ]
      : [
          {
            section: isLnat ? 'Section A : Multiple Choice' : 'Paper 1',
            questions: p1,
            time: p1Time,
          },
          ...(hasPaper2 ? [{
            section: isLnat ? 'Section B : Essay' : 'Paper 2',
            questions: p2,
            time: p2Time,
          }] : []),
        ];
  const sectionCount = tableRows.length;
  const sectionPhrase = sectionCount === 1
    ? 'one section'
    : sectionCount === 2
      ? 'two sections'
      : sectionCount === 3
        ? 'three sections'
        : `${sectionCount} sections`;
  const attemptCount = tableRows[0]?.questions || p1;

  useTmuaPearsonAccessKeys(
    {
      showEndExamModalVisible: showEndExam,
      nextAvailable: !showEndExam,
      endExamAvailable: true,
    },
    {
      onNext: () => { if (!showEndExam) onNext(); },
      onEndExam: () => setShowEndExam(true),
      onConfirmYes: () => { if (showEndExam) onEndExam(); },
      onConfirmNo: () => setShowEndExam(false),
    }
  );

  return (
    <TmuaPearsonShell
      examName={examName}
      title={title || getPearsonPlayerExamTitle(examName, undefined, { tsaSitting, heritageExam })}
      tsaSitting={tsaSitting}
      heritageExam={heritageExam}
      colorSchemeLabel={isLnat ? getPearsonColorSchemeDropdownLabel(examName) : undefined}
      onBackToSelection={onEndExam}
      colorScheme={colorScheme}
      setColorScheme={setColorScheme}
      footerLeft={(
        <TmuaPearsonFooterButton onClick={() => setShowEndExam(true)} icon={<DoorIcon color={T.titleBarFg} />} underline="e" keepLabelOnMobile>
          End Exam
        </TmuaPearsonFooterButton>
      )}
      footerRight={(
        <>
          {lnatMockOverview ? null : (
            <TmuaPearsonFooterButton onClick={onPrevious} icon={<PrevIcon color={T.titleBarFg} />}>
              Previous
            </TmuaPearsonFooterButton>
          )}
          <TmuaPearsonFooterButton variant={nextVariant} onClick={onNext} icon={<NextIcon color={nextIconColor} />} iconPosition="end" underline="n">
            Next
          </TmuaPearsonFooterButton>
        </>
      )}
    >
      {lnatMockOverview ? (
        <LnatMockOverviewBody
          paper1Count={paper1Count}
          paper2Count={paper2Count}
          hasPaper2={hasPaper2}
          modeChoice={modeChoice}
          paper1Time={paper1Time}
          paper2Time={paper2Time}
        />
      ) : (
      <TmuaPearsonInstructionPage>
        <p style={{ margin: '0 0 12px' }}>
          This exam consists of {sectionPhrase}:
        </p>
        <TmuaPearsonInstructionTable rows={tableRows} />
        {Array.isArray(bodyCopy) && bodyCopy.length ? (
          bodyCopy.map((para, idx) => (
            <p key={idx} style={{ margin: '0 0 12px', fontWeight: para.bold ? 700 : 400 }}>
              {para.text}
            </p>
          ))
        ) : isLnat ? (
          <p style={{ margin: '0 0 12px' }}>
            Section A has {p1} multiple-choice questions
            {modeChoice === 'timed' ? ` and ${p1Time}` : ' and is untimed'}.
            {hasPaper2 ? ` Section B has ${p2} essay prompts${modeChoice === 'timed' ? ` and ${p2Time}` : ' and is untimed'}.` : ''}
          </p>
        ) : (
          <>
            <p style={{ margin: '0 0 12px' }}>
              For each question, choose the one answer you consider correct.
            </p>
            <p style={{ margin: '0 0 12px' }}>
              There are no penalties for incorrect responses, only marks for correct answers, so you should attempt all {attemptCount} questions in {sectionCount > 1 ? 'each section' : 'this section'}. Each question is worth one mark.
            </p>
          </>
        )}
        <NextNCopy />
      </TmuaPearsonInstructionPage>
      )}
      {showEndExam ? (
        <TmuaPearsonDialog
          title="End Exam"
          info
          onYes={onEndExam}
          onNo={() => setShowEndExam(false)}
        >
          You have chosen to end this exam. Are you sure you want to end this exam?
        </TmuaPearsonDialog>
      ) : null}
    </TmuaPearsonShell>
  );
}
